import type { IStressTest } from '@/types/rc/miners'
import type { IStressTestForm } from '@/types/rc/miners/forms'
import { createStressTest, deleteStressTests, getStressTests } from '@/services/rc.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'

// Deals with the transformation of the data from the BE to the FE
// For invalidation to apply, the invalidated query should be mounted

export interface CreateStressTestMutation {
  stress_test: IStressTestForm
}

export interface UIStressTest extends IStressTest {
  id: number
  isLatest: boolean
  flaggedDelete: boolean
}

// Stress tests BE to FE transformation - Stateless
const augmentWithUIFlags = (test: IStressTest, i: number) =>
  ({ ...test, flaggedDelete: false, isLatest: i == 0 }) as UIStressTest
const transformStressTests = (data: IStressTest[]) => data.map((test, i) => augmentWithUIFlags(test, i))

export default function useMinerStressTests(minerId: Ref<number>) {
  // Grab queryClient to invalidate queries
  const client = useQueryClient()

  // GET /miners/:id/stress_tests
  const getStressTestsQuery = useQuery({
    queryKey: ['stress-tests', minerId],
    queryFn: () =>
      getStressTests(minerId.value)
        .then(transformStressTests)
        .catch(NotifyErrorWrapper('Failed to fetch stress tests')),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: [],
  })

  // DELETE /miners/:id/stress_tests/:id
  const deleteStressTestsMutation = useMutation({
    mutationFn: ({ ids }: { ids: number[] }): Promise<any> => deleteStressTests(minerId.value, ids),
    onError: NotifyErrorWrapper('Failed to delete stress test'),
    onSuccess: () => {
      NotifySuccess(`Stress test deleted`)
      client.invalidateQueries({ queryKey: ['stress-tests', minerId] })
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
    },
  })

  // POST /miners/:id/stress_tests
  const createStressTestMutation = useMutation({
    mutationFn: ({ stress_test }: CreateStressTestMutation): Promise<any> =>
      createStressTest(minerId.value, stress_test),
    onError: NotifyErrorWrapper('Failed to create stress test'),
    onSuccess: () => {
      NotifySuccess(`Stress test created`)
      client.invalidateQueries({ queryKey: ['stress-tests', minerId] })
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
    },
  })

  return {
    getStressTestsQuery,
    deleteStressTestsMutation,
    createStressTestMutation,
  }
}
